/**
 * Username Prompt Modal
 * Shows when a new user first joins the nightclub to set their display name
 */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { User, Sparkles, Check, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fun random name suggestions
const NAME_SUGGESTIONS = [
  'NeonDancer', 'BassDropper', 'RaveMaster', 'GlowStick', 'VibeLord',
  'BeatRider', 'LightWave', 'SynthKid', 'DropZone', 'FloorShaker',
  'NightOwl', 'PartyPulse', 'RhythmRider', 'SonicBoom', 'GrooveKing'
];

const UsernamePromptModal = ({ 
  isOpen, 
  onComplete,
  onClose,
  user,
  sessionToken 
}) => {
  const [username, setUsername] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const autoCloseTimerRef = useRef(null);
  const countdownRef = useRef(null);

  // Auto-close after 5 minutes
  useEffect(() => {
    if (isOpen) {
      setTimeLeft(300);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Time's up - close the modal
            clearInterval(countdownRef.current);
            onClose?.();
            toast.info('Username prompt closed - you can set it later from the menu');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => {
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    }
  }, [isOpen, onClose]);

  // Format time as mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate a random suggestion on mount
  useEffect(() => {
    if (isOpen && !username) {
      const randomSuggestion = NAME_SUGGESTIONS[Math.floor(Math.random() * NAME_SUGGESTIONS.length)];
      setUsername(randomSuggestion + Math.floor(Math.random() * 100));
    }
  }, [isOpen]);

  // Validate username format
  const validateUsername = (name) => {
    if (name.length < 3) return 'Username must be at least 3 characters';
    if (name.length > 20) return 'Username must be 20 characters or less';
    if (!/^[a-zA-Z0-9_]+$/.test(name)) return 'Only letters, numbers, and underscores allowed';
    return null;
  };

  // Check username availability (debounced)
  useEffect(() => {
    const validationError = validateUsername(username);
    if (validationError) {
      setError(validationError);
      setIsAvailable(null);
      return;
    }

    setError('');
    setIsChecking(true);
    
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/nightclub/check-username?username=${encodeURIComponent(username)}`, {
          headers: sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}
        });
        const data = await res.json();
        setIsAvailable(data.available);
        if (!data.available) {
          setError('Username is already taken');
        }
      } catch (err) {
        console.error('Failed to check username:', err);
        // If check fails, allow the user to try anyway
        setIsAvailable(true);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username, sessionToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateUsername(username);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/nightclub/set-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
        },
        body: JSON.stringify({ username: username.trim() })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Welcome to the club, ${username}!`);
        onComplete(username);
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Failed to set username');
      }
    } catch (err) {
      console.error('Failed to save username:', err);
      setError('Network error - please try again');
    } finally {
      setIsSaving(false);
    }
  };

  const getRandomName = () => {
    const randomSuggestion = NAME_SUGGESTIONS[Math.floor(Math.random() * NAME_SUGGESTIONS.length)];
    setUsername(randomSuggestion + Math.floor(Math.random() * 100));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="w-full max-w-md bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl shadow-purple-500/20 overflow-hidden relative"
        >
          {/* Close Button */}
          <button
            onClick={() => {
              onClose?.();
              toast.info('You can set your username later from the menu');
            }}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Auto-close timer */}
          <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-black/50 text-white/60 text-xs font-mono">
            Closes in {formatTime(timeLeft)}
          </div>

          {/* Header */}
          <div className="p-6 pt-12 text-center bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-b border-purple-500/20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <User className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Welcome to the Club!
            </h2>
            <p className="text-white/60 mt-2 text-sm">
              Choose your display name for the nightclub
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-white/80 text-sm font-medium">Your Username</label>
              <div className="relative">
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username..."
                  className="bg-black/50 border-purple-500/30 text-white text-lg h-12 pr-10 focus:border-purple-400"
                  maxLength={20}
                  autoFocus
                />
                {/* Status indicator */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isChecking && (
                    <div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                  )}
                  {!isChecking && isAvailable === true && !error && (
                    <Check className="w-5 h-5 text-green-400" />
                  )}
                  {!isChecking && (isAvailable === false || error) && (
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  )}
                </div>
              </div>
              
              {/* Error/Status message */}
              {error && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </p>
              )}
              {!error && isAvailable === true && (
                <p className="text-green-400 text-sm flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Username is available!
                </p>
              )}
            </div>

            {/* Random name button */}
            <Button
              type="button"
              onClick={getRandomName}
              variant="outline"
              className="w-full border-purple-500/30 text-purple-300 hover:bg-purple-500/20"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Random Name
            </Button>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={isSaving || isChecking || !isAvailable || !!error}
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Joining...
                </div>
              ) : (
                'Enter the Club'
              )}
            </Button>

            <p className="text-white/40 text-xs text-center">
              You can change your username later in settings
            </p>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UsernamePromptModal;
